import { moveCard as _moveCard } from "@bonliva/react-kanban";
import { BoardDescription, CardDescription } from "./kanban-types";

export function moveCard(
  state: BoardDescription<any>,
  cardId: string,
  destColumnId: string,
  destPosition: number
) {
  const column = state.columns.find((col) =>
    col.cards.find((card) => card.id === cardId)
  );

  if (!column) {
    return state;
  }

  const card = column.cards.find((card) => card.id === cardId);

  if (!card) {
    return state;
  }

  const cardPosition = column.cards.indexOf(card);

  if (column.id === destColumnId && cardPosition === destPosition) {
    return state;
  }

  const nextState = _moveCard(
    state,
    { fromPosition: cardPosition, fromColumnId: column.id },
    {
      toPosition: destPosition,
      toColumnId: destColumnId
    }
  ) as BoardDescription<any>;

  // Sync the moved card's `issue.order` and `issue.columnId` to match its
  // new visual slot. Without this, any later code path that sorts by
  // `issue.order` — a sibling card's subscription patch (UPSERT_CARD), the
  // originator-skip window expiring, or a board refetch from a dialog
  // onClose — re-derives a stale position from the pre-shift cached order
  // and the card visibly drifts. We pick a synthetic midpoint between the
  // new neighbours so a plain `sortBy(order)` keeps the card at its dropped
  // slot until the backend's authoritative order arrives via subscription.
  const destCol = nextState.columns.find((c) => c.id === destColumnId);
  if (destCol && card.issue && typeof card.issue === "object") {
    const idx = destCol.cards.findIndex((c) => c.id === cardId);
    if (idx !== -1) {
      const prev = idx > 0 ? destCol.cards[idx - 1] : null;
      const next = idx < destCol.cards.length - 1 ? destCol.cards[idx + 1] : null;
      const prevOrder = prev?.issue?.order;
      const nextOrder = next?.issue?.order;
      let synthOrder: number | undefined;
      if (typeof prevOrder === "number" && typeof nextOrder === "number") {
        synthOrder = (prevOrder + nextOrder) / 2;
      } else if (typeof prevOrder === "number") {
        synthOrder = prevOrder + 0.5;
      } else if (typeof nextOrder === "number") {
        synthOrder = nextOrder - 0.5;
      }
      const newIssue = {
        ...card.issue,
        columnId: destColumnId,
        ...(synthOrder !== undefined ? { order: synthOrder } : {})
      };
      const updatedCard: CardDescription<any> = { ...card, issue: newIssue };
      destCol.cards = [
        ...destCol.cards.slice(0, idx),
        updatedCard,
        ...destCol.cards.slice(idx + 1)
      ];
    }
  }

  return nextState;
}
